import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";

/**
 * Screen width breakpoints, mirroring `_variables.scss`. Keep the two in sync:
 * logic and styles that gate on the same breakpoint must agree, or elements
 * handed off between them can fall into a gap and disappear.
 */
export const SCREEN_WIDTH = {
  XXS: 320,
  XS: 375,
  S: 450,
  M: 768,
  ML: 900,
  L: 1024,
  XL: 1440,
  XXL: 2560,
} as const;

interface ViewportContextType {
  vw: number;
  vh: number;
  // Alias for `isMaxViewportM` (the mobile-web boundary): always the same
  // value, kept so call sites can speak "mobile" instead of a breakpoint name.
  isMobile: boolean;
  // `isMaxViewport*` mirror CSS `@media (max-width: $x-screen-width)` exactly:
  // inclusive of the breakpoint (vw <= X). Prefer these when the logic has a
  // stylesheet counterpart. `isMaxViewportM` is the "mobile web" boundary (768).
  isMaxViewportXXS: boolean;
  isMaxViewportXS: boolean;
  isMaxViewportS: boolean;
  isMaxViewportM: boolean;
  isMaxViewportML: boolean;
  isMaxViewportL: boolean;
  isMaxViewportXL: boolean;
  isMaxViewportXXL: boolean;
  // `isViewport*` are band flags: the viewport is within [previous breakpoint,
  // this breakpoint) — at or above the one below, and below this one. Use when
  // you want a single size band rather than an open-ended max/min.
  isViewportXXS: boolean;
  isViewportXS: boolean;
  isViewportS: boolean;
  isViewportM: boolean;
  isViewportML: boolean;
  isViewportL: boolean;
  isViewportXL: boolean;
  isViewportXXL: boolean;
}

const ViewportContext = createContext<ViewportContextType>({
  vw: 0,
  vh: 0,
  isMobile: false,
  isMaxViewportXXS: false,
  isMaxViewportXS: false,
  isMaxViewportS: false,
  isMaxViewportM: false,
  isMaxViewportML: false,
  isMaxViewportL: false,
  isMaxViewportXL: false,
  isMaxViewportXXL: false,
  isViewportXXS: false,
  isViewportXS: false,
  isViewportS: false,
  isViewportM: false,
  isViewportML: false,
  isViewportL: false,
  isViewportXL: false,
  isViewportXXL: false,
});

interface ViewportContextProviderProps {
  children: ReactNode;
}

export const ViewportContextProvider = ({
  children,
}: ViewportContextProviderProps) => {
  const [vh, setVh] = useState<number>(0);
  const [vw, setVw] = useState<number>(0);

  useEffect(() => {
    let unmounted = false;

    const onResize = () => {
      if (unmounted) return;
      const newVh = window.innerHeight;
      const newVw = window.innerWidth;

      if (newVh !== vh) setVh(newVh);
      if (newVw !== vw) setVw(newVw);
    };

    onResize();
    window.addEventListener("resize", onResize);

    return () => {
      unmounted = true;
      window.removeEventListener("resize", onResize);
    };
  }, [vh, vw]);

  const value = useMemo<ViewportContextType>(
    () => ({
      vw,
      vh,

      // Alias for isMaxViewportM below.
      isMobile: vw <= SCREEN_WIDTH.M,

      // Max: inclusive, matching `@media (max-width: $x-screen-width)`.
      isMaxViewportXXS: vw <= SCREEN_WIDTH.XXS,
      isMaxViewportXS: vw <= SCREEN_WIDTH.XS,
      isMaxViewportS: vw <= SCREEN_WIDTH.S,
      isMaxViewportM: vw <= SCREEN_WIDTH.M,
      isMaxViewportML: vw <= SCREEN_WIDTH.ML,
      isMaxViewportL: vw <= SCREEN_WIDTH.L,
      isMaxViewportXL: vw <= SCREEN_WIDTH.XL,
      isMaxViewportXXL: vw <= SCREEN_WIDTH.XXL,

      // Band: [previous breakpoint, this breakpoint). Exclusive of the upper
      // bound, so they do NOT line up with a CSS `max-width` query.
      isViewportXXS: vw < SCREEN_WIDTH.XXS,
      isViewportXS: vw >= SCREEN_WIDTH.XXS && vw < SCREEN_WIDTH.XS,
      isViewportS: vw >= SCREEN_WIDTH.XS && vw < SCREEN_WIDTH.S,
      isViewportM: vw >= SCREEN_WIDTH.S && vw < SCREEN_WIDTH.M,
      isViewportML: vw >= SCREEN_WIDTH.M && vw < SCREEN_WIDTH.ML,
      isViewportL: vw >= SCREEN_WIDTH.ML && vw < SCREEN_WIDTH.L,
      isViewportXL: vw >= SCREEN_WIDTH.L && vw < SCREEN_WIDTH.XL,
      isViewportXXL: vw >= SCREEN_WIDTH.XL && vw < SCREEN_WIDTH.XXL,
    }),
    [vw, vh],
  );

  return (
    <ViewportContext.Provider value={value}>
      {children}
    </ViewportContext.Provider>
  );
};

export const useViewport = () => {
  const context = useContext(ViewportContext);

  if (!context) {
    throw new Error(
      "useViewport must be used within a ViewportContextProvider",
    );
  }

  return context;
};
